import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CustomerVehiclePicker } from "@/components/CustomerVehiclePicker";
import { JobCardAPI, BayAPI, BranchAPI, TechnicianAPI, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function JobNewPage() {
    const nav = useNavigate();
    const [search] = useSearchParams();
    const { user } = useAuth();
    const [busy, setBusy] = useState(false);
    const [branches, setBranches] = useState([]);
    const [bays, setBays] = useState([]);
    const [techs, setTechs] = useState([]);
    const [cv, setCV] = useState({ customer: null, vehicle: null });
    const [form, setForm] = useState({
        branch_id: "",
        bay_id: "",
        assigned_technician_user_ids: [],
        complaint_summary: "",
        inspection_notes: "",
        odometer_in: "",
        fuel_level_in: "",
        force: false,
    });

    useEffect(() => {
        BranchAPI.list().then((b) => {
            setBranches(b);
            if (b.length) {
                const pref = user?.default_branch_id || b[0].id;
                setForm((f) => ({ ...f, branch_id: pref }));
            }
        });
        TechnicianAPI.list({ is_active: true }).then((t) => setTechs(t.items || []));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!form.branch_id) return;
        BayAPI.list({ branch_id: form.branch_id, is_active: true }).then((r) => setBays(r.items || []));
    }, [form.branch_id]);

    const visibleTechs = useMemo(() => {
        if (!form.branch_id) return techs;
        return techs.filter((t) => (t.branch_ids || []).includes(form.branch_id));
    }, [techs, form.branch_id]);

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }
    function toggleTech(uid) {
        set("assigned_technician_user_ids", form.assigned_technician_user_ids.includes(uid)
            ? form.assigned_technician_user_ids.filter((x) => x !== uid)
            : [...form.assigned_technician_user_ids, uid]);
    }

    async function save() {
        if (!cv.customer || !cv.vehicle) { toast.error("Pick a customer and a vehicle"); return; }
        setBusy(true);
        try {
            const body = {
                branch_id: form.branch_id,
                customer_id: cv.customer.id,
                vehicle_id: cv.vehicle.id,
                bay_id: form.bay_id || undefined,
                assigned_technician_user_ids: form.assigned_technician_user_ids,
                complaint_summary: form.complaint_summary || undefined,
                inspection_notes: form.inspection_notes || undefined,
                odometer_in: form.odometer_in ? Number(form.odometer_in) : undefined,
                fuel_level_in: form.fuel_level_in ? Number(form.fuel_level_in) : undefined,
                force: !!form.force,
            };
            const j = await JobCardAPI.create(body);
            toast.success(`Job ${j.job_number} created`);
            nav(`/jobs/${j.id}`);
        } catch (e) {
            const msg = extractError(e);
            // Surface owner override hint
            if (msg.toLowerCase().includes("open job")) {
                toast.error(msg + (user?.role === "owner" ? " — toggle Force to override." : ""));
            } else {
                toast.error(msg);
            }
        } finally { setBusy(false); }
    }

    return (
        <div className="space-y-6 max-w-4xl animate-fade-in" data-testid="job-new-page">
            <header className="flex items-center justify-between">
                <div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">New walk-in job</h1>
                    <p className="mt-1 text-sm text-zinc-500">Opens a job card directly without a preceding appointment.</p>
                </div>
                <Link to="/jobs" className="text-sm text-zinc-500 hover:underline">← Back</Link>
            </header>

            <Card className="p-6">
                <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Customer &amp; vehicle</h2>
                <CustomerVehiclePicker value={cv} onChange={setCV} testid="job-cvpicker" />
            </Card>

            <Card className="p-6">
                <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Service</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="Branch" required>
                        <select value={form.branch_id} onChange={(e) => set("branch_id", e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="job-branch">
                            {branches.map((b) => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
                        </select>
                    </Field>
                    <Field label="Bay">
                        <select value={form.bay_id} onChange={(e) => set("bay_id", e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="job-bay">
                            <option value="">— No bay yet —</option>
                            {bays.map((b) => <option key={b.id} value={b.id}>{b.code} · {b.bay_type}</option>)}
                        </select>
                    </Field>
                    <Field label="Odometer in (km)">
                        <Input type="number" min="0" value={form.odometer_in} onChange={(e) => set("odometer_in", e.target.value)} placeholder="58210" data-testid="job-odo-in" />
                    </Field>
                    <Field label="Fuel level (%)">
                        <Input type="number" min="0" max="100" value={form.fuel_level_in} onChange={(e) => set("fuel_level_in", e.target.value)} placeholder="55" data-testid="job-fuel-in" />
                    </Field>
                </div>
            </Card>

            <Card className="p-6">
                <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Technicians ({form.assigned_technician_user_ids.length})</h2>
                {visibleTechs.length === 0 ? (
                    <p className="text-sm text-zinc-500">No technicians available for this branch.</p>
                ) : (
                    <div className="flex flex-wrap gap-1.5">
                        {visibleTechs.map((t) => (
                            <button key={t.id} type="button" onClick={() => toggleTech(t.user_id)} data-testid={`job-tech-${t.user_id}`}
                                className={`rounded-md border px-2.5 py-1.5 text-xs ${form.assigned_technician_user_ids.includes(t.user_id) ? "border-brand-red bg-brand-redSoft text-brand-redDeep dark:bg-brand-red/15 dark:text-brand-red" : "border-zinc-200 dark:border-zinc-800"}`}>
                                <span className="font-medium">{t.user?.name || t.employee_code}</span>
                                <span className="ml-1 text-zinc-500 font-mono">· {(t.skills || []).slice(0, 2).join(",")}</span>
                            </button>
                        ))}
                    </div>
                )}
            </Card>

            <Card className="p-6">
                <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Notes</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="Customer complaint">
                        <Textarea value={form.complaint_summary} onChange={(e) => set("complaint_summary", e.target.value)} rows={3} data-testid="job-complaint" />
                    </Field>
                    <Field label="Inspection notes">
                        <Textarea value={form.inspection_notes} onChange={(e) => set("inspection_notes", e.target.value)} rows={3} data-testid="job-inspection" />
                    </Field>
                </div>
                {user?.role === "owner" && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-zinc-500">
                        <input id="force" type="checkbox" checked={form.force} onChange={(e) => set("force", e.target.checked)} data-testid="job-force" />
                        <label htmlFor="force">Force open even if vehicle has an open job (owner-only override)</label>
                    </div>
                )}
            </Card>

            <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" onClick={() => nav(-1)}>Cancel</Button>
                <Button onClick={save} disabled={busy || !cv.customer || !cv.vehicle || !form.branch_id} className="bg-brand-red text-white hover:bg-brand-redDeep" data-testid="job-save">
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Open job card
                </Button>
            </div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                {label}{required && <span className="ml-0.5 text-brand-red">*</span>}
            </Label>
            {children}
        </div>
    );
}
